// HelpAssistant/fabPosition.js
//
// Pure geometry for the floating Help launcher. No React, no DOM reads — every
// input is passed in, so this is unit-testable against REAL measured rects.
//
// WHY a "consent obstruction" input exists (measured, not theoretical):
// On staging (bundle main.a07a2d9a.js, 2026-08-03) the FAB's fixed bottom-right
// corner sat ON TOP of the GDPR consent banner's Accept button. Measured
// getBoundingClientRect intersections at the time:
//     1440x900  ix=-90            -> no overlap (control)
//     1024x768  ix=56  iy=44      -> 2464 px^2 overlap
//      768x1024 ix=56  iy=42      -> 2352 px^2 overlap
//      390x844  ix=56  iy=44      -> 2464 px^2 overlap
// and `document.elementFromPoint` at Accept's own centre returned the FAB's
// help-icon <svg>, not the button — i.e. a user clicking "Accept" opened the
// Help Assistant instead of consenting (the FAB's stacking layer is z-index
// 13000; the banner's is 1150, so the FAB wins). That is the G-139 overlap
// class: a control that is visible but not clickable because something else
// owns its pixels.
//
// The fix is positional, not a z-index race: while the consent banner is
// mounted its height is passed in as `obstructionHeight` and the FAB is clamped
// to sit fully ABOVE it, with a gap. Zero intersection then holds by
// construction at every viewport width — it is not a per-breakpoint tweak that
// a new breakpoint could slip past. When the banner unmounts (a choice is
// made), obstructionHeight is 0 and the FAB returns to its normal corner.

/** Rendered FAB diameter (MUI Fab, > the 44px touch-target floor). */
export const FAB_SIZE = 56;

/** Corner inset at >= sm, matching the original `right/bottom: 24` styling. */
export const FAB_MARGIN = 24;

/** Corner inset below sm (600px), matching the original `right/bottom: 16`. */
export const FAB_MARGIN_XS = 16;

/** MUI's sm breakpoint — the width at which the corner inset changes. */
export const FAB_MARGIN_BREAKPOINT = 600;

/** Clear air kept between the FAB and any obstruction it must not cover. */
export const OBSTRUCTION_GAP = 12;

/** Hard minimum inset from any viewport edge once the user is dragging. */
export const EDGE_MARGIN = 8;

/**
 * T22.227 (phone UX audit UX-27, MEASURED live web-stg main.dd06f65e.js at
 * 390x844): below sm the 56px round FAB plus its 32px dismiss X made a 66x66px
 * block parked 6px off the right edge, so it sat over 72px of every page's
 * right-hand side — on /help it covered the "Message support" card, and while
 * scrolling it covered FAQ rows, the contact form's labels and Storage text.
 *
 * On a phone the launcher is therefore a narrow EDGE TAB docked flush against
 * the right edge: a 32px-wide column holding the dismiss X's 32px hit area on
 * top of a 48px-tall help tab. Both stay at the app's 32px touch-target floor
 * (DL-096 / ISP-4021); only the footprint shrinks (32x80 vs 66x66, and 32px of
 * page width instead of 72px).
 */
//
// T22.177 (MEASURED web-stg main.4f2c9ca4.js, Chromium 390x844): that 32px
// column left BOTH phone controls under the 44px touch target the RN app uses
// (help tab 32x48, dismiss X 32x32) on every page. The column is now 44px wide
// and the X's hit area 44px tall: 44x92, still far narrower than the 66x66
// round FAB block T22.227 replaced, and still flush to the edge.
//
// T22.227 recheck (MEASURED web-stg main.e655fe69.js, Chromium 390x844 after
// FE#7503): the 44px-wide column started at x=346, LEFT of the Discover card
// heart's centre (x=349: card right edge 374 minus the heart's 24px centre
// inset). At 20 of 36 scroll offsets elementFromPoint(heart centre) returned
// the help tab or its X, and a tap there hid the help assistant instead of
// favoriting the wheel (on main.ed3bd179, 32px wide: 0 of 32, tap favorited).
// The heart centre sits 40px from a phone's right edge at every phone width,
// so the column stays 32px WIDE; the 44px touch target is kept on the axis
// that covers nothing -- the X's hit area is 44px TALL (32x92 column).
export const PHONE_TAP_TARGET = 44;
export const PHONE_TAB_WIDTH = 32;
export const PHONE_TAB_HEIGHT = 48;
export const PHONE_DISMISS_HEIGHT = PHONE_TAP_TARGET;

/**
 * The launcher's box for a viewport width: `{ width, height, edgeMargin }`,
 * where edgeMargin is how far the box rests from the right edge. Phones get the
 * flush edge tab; sm and up keep the 56px round FAB exactly as before.
 */
export const launcherBoxForWidth = (viewportWidth, fabSize = FAB_SIZE) =>
  viewportWidth < FAB_MARGIN_BREAKPOINT
    ? {
        width: PHONE_TAB_WIDTH,
        height: PHONE_DISMISS_HEIGHT + PHONE_TAB_HEIGHT,
        edgeMargin: 0,
      }
    : { width: fabSize, height: fabSize, edgeMargin: EDGE_MARGIN };

/** The corner inset used at a given viewport width. */
export const marginForWidth = (viewportWidth) =>
  viewportWidth < FAB_MARGIN_BREAKPOINT ? FAB_MARGIN_XS : FAB_MARGIN;

/**
 * Where the FAB sits for a user who has never dragged it: the bottom-right
 * corner, byte-equivalent to the pre-change `position: fixed; right; bottom`.
 */
export const defaultFabPosition = ({
  viewportWidth,
  viewportHeight,
  fabSize = FAB_SIZE,
  fabWidth = fabSize,
  fabHeight = fabSize,
  edgeMargin,
}) => {
  const margin = marginForWidth(viewportWidth);
  return {
    left: viewportWidth - fabWidth - (edgeMargin === 0 ? 0 : margin),
    top: viewportHeight - fabHeight - margin,
  };
};

/**
 * T22.757 (MEASURED live web-stg main.cfad8a37.js, Settings > Subscription &
 * Billing, iy-qa P_PRO): the plan-card row runs to within 24-58px of the
 * viewport's right edge, but the launcher's resting box starts 80px (desktop
 * FAB) / 32px (phone edge tab) from it. At 1280x800 the FAB sat on the ProSuite
 * card's "Customizable Ad Space (Unlimited)" row (21x34px overlap), at 1366x768
 * on it too (18x40px), and at 390x844 the tab covered 6px of every stacked
 * card's text. Scrolling the page does not help: the FAB is fixed, so whatever
 * passes the bottom-right corner passes under it.
 *
 * The x where the launcher's DEFAULT (never-dragged) box starts, for a
 * viewport width -- the same corner defaultFabPosition renders.
 */
export const defaultLauncherLeft = (viewportWidth) => {
  const box = launcherBoxForWidth(viewportWidth);
  return defaultFabPosition({
    viewportWidth,
    viewportHeight: 0,
    fabWidth: box.width,
    fabHeight: box.height,
    edgeMargin: box.edgeMargin,
  }).left;
};

/**
 * Right-hand gutter (px, integer, >= 0) a content block whose unreserved right
 * edge is `contentRight` must reserve so it ends OBSTRUCTION_GAP short of the
 * launcher's resting box -- i.e. the launcher's column never overlaps it at any
 * scroll offset.
 */
export const launcherRightGutter = ({ viewportWidth, contentRight }) =>
  Math.max(
    0,
    Math.ceil(contentRight - (defaultLauncherLeft(viewportWidth) - OBSTRUCTION_GAP))
  );

/**
 * Keep a position inside the viewport, above any bottom-anchored obstruction
 * (the consent banner), LEFT of any right-docked obstruction (the builder's
 * rail — see railObstructionWidth below), and BELOW the app header band (see
 * topExclusionHeight). Returns integer px.
 *
 * `obstructionHeight`  height of a bottom-docked, full-width element the FAB
 *                      must never cover; 0 when there is none.
 * `railInsetWidth`     width of a RIGHT-docked, full-height column the FAB must
 *                      never cover; 0 when there is none. A right-docked column
 *                      is cleared HORIZONTALLY — clearing it vertically is what
 *                      put the FAB on the app header (see the header note on
 *                      railObstructionWidth below).
 * `topExclusionHeight` height of the top app header band. The FAB may never be
 *                      clamped ONTO it, whatever else is pushing it up.
 */
export const clampFabPosition = ({
  left,
  top,
  viewportWidth,
  viewportHeight,
  fabSize = FAB_SIZE,
  fabWidth = fabSize,
  fabHeight = fabSize,
  edgeMargin = EDGE_MARGIN,
  obstructionHeight = 0,
  railInsetWidth = 0,
  topExclusionHeight = 0,
}) => {
  const maxLeftViewport = viewportWidth - fabWidth - edgeMargin;
  const maxLeftRail =
    railInsetWidth > 0
      ? viewportWidth - railInsetWidth - OBSTRUCTION_GAP - fabWidth
      : maxLeftViewport;
  // FLOOR, not round: rounding a fractional bound UP would spend part of the
  // clearance gap the bound exists to guarantee.
  const maxLeft = Math.max(EDGE_MARGIN, Math.floor(Math.min(maxLeftViewport, maxLeftRail)));

  const maxTopViewport = viewportHeight - fabHeight - EDGE_MARGIN;
  const maxTopObstruction =
    obstructionHeight > 0
      ? viewportHeight - obstructionHeight - OBSTRUCTION_GAP - fabHeight
      : maxTopViewport;
  // THE HEADER FLOOR. Every vertical obstruction pushes the FAB UP, and the top
  // of the viewport is where the app header lives — so "clear the obstruction"
  // and "do not eat the header's controls" are the same constraint seen from
  // two ends. MEASURED (web-dev.iyuga.net/builder, bundle main.5419c4a9.js,
  // 2026-09-07, 1024x780 AND 1024x768): with only the EDGE_MARGIN floor the FAB
  // landed at 944,8 — 2912px^2 on the header, 2492.33px^2 (87%) on
  // [aria-label="Account menu"], and elementFromPoint at the account button's
  // own centre returned the FAB's svg, so Logout/Settings/Media Library/
  // Analytics/Themes/Add account were all unreachable. The floor is only
  // applied while the FAB still fits below it, so it can never push the FAB
  // off the bottom of a very short viewport instead.
  const headerFloor =
    topExclusionHeight > 0 ? topExclusionHeight + OBSTRUCTION_GAP : EDGE_MARGIN;
  const minTop =
    headerFloor + fabHeight <= viewportHeight - EDGE_MARGIN
      ? Math.max(EDGE_MARGIN, Math.ceil(headerFloor))
      : EDGE_MARGIN;
  const maxTop = Math.max(minTop, Math.min(maxTopViewport, maxTopObstruction));
  return {
    // A flush edge tab (edgeMargin 0, phones — T22.227) is DOCKED: it only
    // moves up and down the right edge. Free horizontal placement left a saved
    // position from the old 56px bubble (or any drag) floating the tab off the
    // edge with its flat side exposed (review FE#7096, measured live 390x844).
    left: Math.round(
      edgeMargin === 0 ? maxLeft : Math.max(EDGE_MARGIN, Math.min(left, maxLeft))
    ),
    top: Math.round(Math.max(minTop, Math.min(top, maxTop))),
  };
};

/**
 * The single resolver the component renders from: a persisted position when the
 * user has dragged one, the default corner otherwise, in both cases clamped
 * into the viewport and above any obstruction.
 */
export const resolveFabPosition = ({
  persisted,
  viewportWidth,
  viewportHeight,
  fabSize = FAB_SIZE,
  fabWidth = fabSize,
  fabHeight = fabSize,
  edgeMargin = EDGE_MARGIN,
  obstructionHeight = 0,
  railInsetWidth = 0,
  topExclusionHeight = 0,
}) => {
  const hasPersisted =
    persisted &&
    Number.isFinite(Number(persisted.left)) &&
    Number.isFinite(Number(persisted.top));
  const base = hasPersisted
    ? { left: Number(persisted.left), top: Number(persisted.top) }
    : defaultFabPosition({
        viewportWidth,
        viewportHeight,
        fabWidth,
        fabHeight,
        edgeMargin,
      });
  return clampFabPosition({
    ...base,
    viewportWidth,
    viewportHeight,
    fabWidth,
    fabHeight,
    edgeMargin,
    obstructionHeight,
    railInsetWidth,
    topExclusionHeight,
  });
};

/**
 * Geometry-only rect intersection area, in px^2 (0 when they do not overlap).
 * Exported so a test can assert the SAME arithmetic a browser's
 * getBoundingClientRect comparison would produce.
 */
export const intersectionArea = (a, b) => {
  if (!a || !b) return 0;
  const ix = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const iy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return ix > 0 && iy > 0 ? ix * iy : 0;
};

/** The FAB's rect for a resolved {left, top}. */
export const fabRect = ({ left, top }, fabSize = FAB_SIZE) => ({
  left,
  top,
  right: left + fabSize,
  bottom: top + fabSize,
});

/**
 * gordon-glitch-help-button-covers-capture-rail-item-1024w
 * fix-help-fab-still-covers-a-rail-item-after-5236
 * fix-help-fab-covers-account-menu-1024w-short   <- this revision
 *
 * MEASURED DEFECT (web-dev.iyuga.net/builder, uid 12675, bundle
 * main.407d5cad.js, 2026-09-06): the Segment Builder's right-hand tool rail
 * (SideMenu) and this FAB's default bottom-right corner both live in the same
 * screen corner. At full-height desktop viewports there is enough vertical
 * room between the rail's last item and the viewport bottom that they never
 * meet, but at the tablet-landscape heights the builder is actually used at
 * (768-900px, e.g. 1024x780, 1024x768) the rail's items sit low enough that
 * the FAB's corner rect lands on top of one of them:
 *   1024x780  FAB 944,700-1000,756   Capture 962.5625,631.171875-1024,711.171875  -> ix=418.25
 *   1024x768  FAB 944,688-1000,744   Capture 962.5625,631.171875-1024,711.171875  -> ix=867.50
 *    820x1180 FAB 740,1100-796,1156  Capture 760,665.5625-820,745.5625           -> ix=0 (control)
 *
 * WHY THIS IS A *WIDTH*, NOT A HEIGHT (the correction this revision makes).
 * #5236/#5242 modelled the rail as a bottom-docked obstruction and reserved
 * `viewportHeight - railTop` of vertical space. The rail is not bottom-docked:
 * it is a RIGHT-docked, full-height column starting directly under the app
 * header, so that reservation is nearly the whole viewport and the clamp ran
 * the FAB straight up into the header. MEASURED live on bundle main.5419c4a9.js
 * (2026-09-07), rail union 962.5625,60-1024,711.171875 at 1024x780:
 *   obstructionHeight = ceil(780 - 60) = 720
 *   maxTopObstruction = 780 - 720 - 12 - 56 = -68  -> clamped to EDGE_MARGIN 8
 *   FAB 944,8-1000,64  ->  2912px^2 on the app header (0,0-1024,60)
 *                          2492.33px^2 (87%) on [aria-label="Account menu"]
 *   document.elementFromPoint at the Account menu's own centre returned the
 *   FAB's help-icon <svg>: clicking the avatar opened the Help Assistant, and
 *   Logout / Settings / Media Library / Analytics / Themes / Add account were
 *   unreachable from the builder at that size. The dismiss badge clipped at
 *   y=-2. It traded one G-139 overlap for a worse one.
 *
 * A right-docked column is cleared SIDEWAYS. Reserving its width keeps the FAB
 * bottom-anchored where it belongs and cannot interact with the header at all:
 *   1024x780 (fixed)  railInsetWidth = ceil(1024 - 962.5625) = 62
 *                     maxLeft = 1024 - 62 - 12 - 56 = 894
 *                     FAB 894,700-950,756  -> 0 on every rail item, 0 on the
 *                     header, 0 on the Account menu, badge fully on screen.
 *
 * Engagement is unchanged and still gated: the inset is only produced when the
 * default-corner FAB rect would actually reach the measured rail items
 * (inflated by OBSTRUCTION_GAP so it clears BEFORE pixels touch). At 1440x900
 * the rail's items end at y=723.17 while the FAB rests at y=820, so this
 * returns 0 and the FAB does not move — that is the positive control, measured
 * green in the same runs.
 */
export const railObstructionWidth = ({
  railItemRect,
  viewportWidth,
  viewportHeight,
  fabSize = FAB_SIZE,
}) => {
  if (!railItemRect) return 0;
  const candidate = fabRect(
    defaultFabPosition({ viewportWidth, viewportHeight, fabSize }),
    fabSize
  );
  const inflated = {
    left: candidate.left - OBSTRUCTION_GAP,
    right: candidate.right,
    top: candidate.top,
    bottom: candidate.bottom,
  };
  if (intersectionArea(inflated, railItemRect) <= 0) return 0;
  return Math.max(0, Math.ceil(viewportWidth - railItemRect.left));
};

/** Gap between the FAB and the open Help panel. */
export const PANEL_GAP = 12;

/** The panel's natural height (HelpPanel caps itself at 520px on sm+). */
export const PANEL_HEIGHT_ESTIMATE = 520;

/** Below this the panel can't show header + a message + input, so stop shrinking. */
export const PANEL_MIN_HEIGHT = 160;

/**
 * Where the open panel goes relative to the FAB, and how tall it may be, so it
 * always fits inside the VISIBLE viewport.
 *
 * T22.573: MEASURED live on web-stg (main.31c24220.js, 384x740 phone): when the
 * on-screen keyboard opened the visible height dropped to 420px, the FAB was
 * re-clamped to top 366, and the old rule `anchorBelow = top < 532` flipped the
 * 518px panel BELOW the FAB — panel top 416, 4px of it on screen, 0px of the
 * conversation. Pick the side with more room and cap the height to that room.
 */
export const panelPlacement = ({
  top,
  fabHeight,
  viewportHeight,
  gap = PANEL_GAP,
  edge = EDGE_MARGIN,
  estimate = PANEL_HEIGHT_ESTIMATE,
}) => {
  const spaceAbove = top - gap - edge;
  const spaceBelow = viewportHeight - (top + fabHeight + gap) - edge;
  // Prefer above (the default corner); go below only when the panel doesn't
  // fit above AND there is more room below (a FAB dragged near the top).
  const anchorBelow = spaceAbove < estimate && spaceBelow > spaceAbove;
  const room = anchorBelow ? spaceBelow : spaceAbove;
  return {
    anchorBelow,
    maxHeight: Math.round(Math.max(PANEL_MIN_HEIGHT, room)),
  };
};
